/**
 * Swishlist renders UI from app settings (often Polish only).
 * Applies theme locale strings to wishlist buttons and panel.
 */
(function () {
  const strings = window.themeVariables?.strings;
  const labelAdd = strings?.wishlistAdd;
  const labelAdded = strings?.wishlistAdded;
  const panel = strings?.wishlistPanel;

  if (!labelAdd || !labelAdded || labelAdd.includes('Translation missing')) {
    return;
  }

  const BUTTON_SELECTOR = '.st-wishlist-button';
  const PANEL_SELECTOR = '.st-wishlist-details';
  const WISHLIST_SCOPE = [
    BUTTON_SELECTOR,
    PANEL_SELECTOR,
    '.st-wishlist-details_empty',
    '.st-wishlist-save-your-wishlist',
    '.st-product-card_group',
    '.shopify-app-block'
  ].join(', ');

  let isApplying = false;
  let pendingFrame = null;

  function setText(element, text) {
    if (element && text && element.textContent !== text) {
      element.textContent = text;
    }
  }

  function setAria(element, text) {
    if (!element || !text) {
      return;
    }

    if (element.getAttribute('aria-label') !== text) {
      element.setAttribute('aria-label', text);
    }

    if (element.getAttribute('title') !== text) {
      element.setAttribute('title', text);
    }
  }

  function localizeEmailOption(button, ariaLabel, optionTitle, description) {
    if (!button) {
      return;
    }

    setAria(button, ariaLabel);
    setText(button.querySelector('.st-email-options-popup-option-title'), optionTitle);
    setText(button.querySelector('.st-email-options-popup-option-description'), description);
  }

  function applyButtonLabels(button) {
    if (!button.classList.contains('st-initialized')) {
      return;
    }

    const isAdded = button.classList.contains('st-is-added');
    const text = isAdded ? labelAdded : labelAdd;
    const labelEl = button.querySelector('.st-label');

    if (
      labelEl?.textContent === text &&
      button.getAttribute('aria-label') === text &&
      button.dataset.label === labelAdd &&
      button.dataset.labelAdded === labelAdded
    ) {
      return;
    }

    button.dataset.label = labelAdd;
    button.dataset.labelAdded = labelAdded;
    setAria(button, text);

    if (labelEl) {
      labelEl.textContent = text;
    }
  }

  function localizeEmptyState(root = document) {
    if (!panel?.emptyTitle) {
      return;
    }

    root.querySelectorAll('.st-wishlist-details_empty').forEach((empty) => {
      const titleEl = empty.querySelector('p b');
      const paragraphs = empty.querySelectorAll('p');
      const descriptionEl = paragraphs.length > 1 ? paragraphs[1] : null;
      const ctaEl = empty.querySelector('.st-wishlist-details_empty_actions .st-atc-button');

      setText(titleEl, panel.emptyTitle);
      setText(descriptionEl, panel.emptyDescription);
      setText(ctaEl, panel.emptyCta);
    });
  }

  function localizePanel(panelRoot) {
    if (!panel?.title || !panelRoot) {
      return;
    }

    setText(
      panelRoot.querySelector('.st-wishlist-details_title .st-h3, .st-wishlist-details_title .h2'),
      panel.title
    );
    setAria(panelRoot.querySelector('.st-close-button'), panel.close);

    const shareLabels = {
      facebook: panel.shareFacebook,
      pinterest: panel.sharePinterest,
      twitter: panel.shareTwitter,
      whatsapp: panel.shareWhatsapp,
      email: panel.shareEmail,
      copy_link: panel.copyClipboard
    };

    panelRoot.querySelectorAll('.st-share-button').forEach((button) => {
      const type = button.dataset.type;

      if (shareLabels[type]) {
        setAria(button, shareLabels[type]);
      }
    });

    setText(panelRoot.querySelector('.st-email-options-popup-title'), panel.emailMethodTitle);

    localizeEmailOption(
      panelRoot.querySelector('#stEmailModalMailClientButton'),
      panel.emailClient,
      panel.mailClient,
      panel.mailClientDescription
    );
    localizeEmailOption(
      panelRoot.querySelector('#stEmailModalGmailButton'),
      panel.gmailWeb,
      panel.gmailWeb,
      panel.openInBrowser
    );
    localizeEmailOption(
      panelRoot.querySelector('#stEmailModalOutlookButton'),
      panel.outlookWeb,
      panel.outlookWeb,
      panel.outlookWebDescription
    );
    localizeEmailOption(
      panelRoot.querySelector('#stEmailModalCopyButton'),
      panel.copyShareableLink,
      panel.copyLink,
      panel.copyShareableLink
    );

    panelRoot.querySelectorAll('.st-product-card_group .st-atc-button').forEach((button) => {
      setText(button, panel.moreInfo);
    });

    panelRoot.querySelectorAll('.st-wishlist-save-your-wishlist a').forEach((link) => {
      setText(link, panel.loginSync);
    });

    localizeEmptyState(panelRoot);
  }

  function isWishlistNode(node) {
    if (node.nodeType !== 1) {
      return false;
    }

    if (node.matches?.(WISHLIST_SCOPE)) {
      return true;
    }

    return Boolean(node.querySelector?.(BUTTON_SELECTOR + ', ' + PANEL_SELECTOR + ', .st-wishlist-details_empty'));
  }

  function processRoot(root) {
    root.querySelectorAll(BUTTON_SELECTOR).forEach(applyButtonLabels);
    root.querySelectorAll(PANEL_SELECTOR).forEach(localizePanel);
    localizeEmptyState(root);
  }

  function runLocalization() {
    if (isApplying) {
      return;
    }

    isApplying = true;

    try {
      processRoot(document);
    } finally {
      isApplying = false;
    }
  }

  function scheduleProcess(nodes) {
    const wishlistNodes = nodes.filter(isWishlistNode);

    if (!wishlistNodes.length) {
      return;
    }

    if (pendingFrame) {
      return;
    }

    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = null;
      runLocalization();
    });
  }

  function scheduleRetries() {
    [300, 1000, 2500].forEach((delay) => {
      window.setTimeout(runLocalization, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      runLocalization();
      scheduleRetries();
    });
  } else {
    runLocalization();
    scheduleRetries();
  }

  const observer = new MutationObserver((mutations) => {
    if (isApplying) {
      return;
    }

    let shouldRun = false;
    const nodesToProcess = [];

    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target.matches?.(BUTTON_SELECTOR)) {
        shouldRun = true;
        continue;
      }

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          nodesToProcess.push(node);
        }
      });
    }

    if (nodesToProcess.length) {
      scheduleProcess(nodesToProcess);
    } else if (shouldRun) {
      scheduleProcess([document.body]);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class']
  });

  document.body.addEventListener(
    'click',
    (event) => {
      const copyButton = event.target.closest('.st-share-button[data-type="copy_link"]');

      if (!copyButton || !panel?.copied) {
        return;
      }

      window.setTimeout(() => {
        const copiedLabel = copyButton.querySelector('span');

        if (copiedLabel?.textContent.trim()) {
          copiedLabel.textContent = panel.copied;
        }
      }, 100);
    },
    true
  );
})();
